/**
 * Tasks Command
 * Manage background agent tasks
 */

import kleur from 'kleur';
import { getBackgroundAgentManager, BackgroundTask } from '../../agents/BackgroundAgentManager';

export async function runTasks(args: string[]): Promise<void> {
  const bgManager = getBackgroundAgentManager();
  const subCommand = args[0] || 'list';

  switch (subCommand) {
    case 'list':
    case 'ls':
      listTasks();
      break;

    case 'show':
      if (args[1]) {
        showTask(args[1]);
      } else {
        console.log(kleur.yellow('Usage: /tasks show <task-id>'));
      }
      break;

    case 'cancel':
      if (args[1]) {
        cancelTask(args[1]);
      } else {
        console.log(kleur.yellow('Usage: /tasks cancel <task-id>'));
      }
      break;

    case 'delete':
      if (args[1]) {
        deleteTask(args[1]);
      } else {
        console.log(kleur.yellow('Usage: /tasks delete <task-id>'));
      }
      break;

    case 'clear':
      clearCompletedTasks();
      break;

    case 'queue':
      showQueueStatus();
      break;

    case 'help':
    default:
      showHelp();
  }
}

function listTasks(): void {
  const bgManager = getBackgroundAgentManager();
  const tasks = bgManager.getAllTasks();
  const status = bgManager.getQueueStatus();

  console.log(kleur.cyan('\n📋 Background Tasks'));
  console.log(kleur.gray('─'.repeat(60)));

  console.log(
    kleur.gray(`Queue: `) +
    kleur.yellow(`${status.pending} pending`) + kleur.gray(' | ') +
    kleur.blue(`${status.running} running`) + kleur.gray(' | ') +
    kleur.green(`${status.completed} completed`) + kleur.gray(' | ') +
    kleur.red(`${status.failed} failed`)
  );
  console.log();

  if (tasks.length === 0) {
    console.log(kleur.gray('No background tasks'));
    console.log(kleur.gray('Start a task with: & <prompt>'));
    return;
  }

  // Sort by status (running first, then pending, then completed)
  const sortedTasks = [...tasks].sort((a, b) => {
    const statusOrder = { running: 0, pending: 1, failed: 2, completed: 3, cancelled: 4 };
    return statusOrder[a.status] - statusOrder[b.status];
  });

  for (const task of sortedTasks) {
    printTaskSummary(task);
  }

  console.log(kleur.gray('─'.repeat(60)));
  console.log(kleur.cyan('Commands:'));
  console.log(kleur.gray('  /tasks show <id>    - Show task details'));
  console.log(kleur.gray('  /tasks cancel <id>  - Cancel a running task'));
  console.log(kleur.gray('  /tasks clear        - Clear completed tasks'));
}

function printTaskSummary(task: BackgroundTask): void {
  const statusIcon = getStatusIcon(task.status);
  const time = formatTimestamp(task.createdAt);
  const prompt = task.prompt.substring(0, 40) + (task.prompt.length > 40 ? '...' : '');

  console.log(
    statusIcon + ' ' +
    kleur.yellow(task.id.substring(0, 12)) +
    kleur.gray(` (${time})`)
  );
  console.log(kleur.gray(`   ${prompt}`));

  if (task.status === 'running' && task.progress !== undefined) {
    const progressBar = createProgressBar(task.progress);
    console.log(kleur.gray(`   ${progressBar} ${task.progress}%`));
    if (task.progressMessage) {
      console.log(kleur.gray(`   ${task.progressMessage}`));
    }
  }

  if (task.status === 'failed' && task.error) {
    console.log(kleur.red(`   Error: ${task.error.substring(0, 50)}`));
  }

  console.log();
}

function showTask(id: string): void {
  const bgManager = getBackgroundAgentManager();
  const tasks = bgManager.getAllTasks();
  const task = tasks.find(t => t.id === id || t.id.startsWith(id));

  if (!task) {
    console.log(kleur.red(`Task not found: ${id}`));
    return;
  }

  console.log(kleur.cyan('\n📋 Task Details'));
  console.log(kleur.gray('─'.repeat(60)));

  console.log(kleur.white('ID: ') + kleur.yellow(task.id));
  console.log(kleur.white('Status: ') + getStatusIcon(task.status) + ' ' + task.status);
  console.log(kleur.white('Created: ') + formatTimestamp(task.createdAt));

  if (task.startedAt) {
    console.log(kleur.white('Started: ') + formatTimestamp(task.startedAt));
  }

  if (task.completedAt) {
    console.log(kleur.white('Completed: ') + formatTimestamp(task.completedAt));
  }

  console.log();
  console.log(kleur.white('Prompt:'));
  console.log(kleur.gray(task.prompt));

  if (task.progress !== undefined) {
    console.log();
    console.log(kleur.white('Progress: ') + createProgressBar(task.progress) + ` ${task.progress}%`);
    if (task.progressMessage) {
      console.log(kleur.gray(task.progressMessage));
    }
  }

  if (task.result) {
    console.log();
    console.log(kleur.white('Result:'));
    console.log(kleur.green(task.result));
  }

  if (task.error) {
    console.log();
    console.log(kleur.white('Error:'));
    console.log(kleur.red(task.error));
  }

  if (task.agentType) {
    console.log();
    console.log(kleur.white('Agent Type: ') + task.agentType);
  }

  if (task.metadata && Object.keys(task.metadata).length > 0) {
    console.log();
    console.log(kleur.white('Metadata:'));
    console.log(kleur.gray(JSON.stringify(task.metadata, null, 2)));
  }
}

function cancelTask(id: string): void {
  const bgManager = getBackgroundAgentManager();
  const tasks = bgManager.getAllTasks();
  const task = tasks.find(t => t.id === id || t.id.startsWith(id));

  if (!task) {
    console.log(kleur.red(`Task not found: ${id}`));
    return;
  }

  if (task.status !== 'pending' && task.status !== 'running') {
    console.log(kleur.yellow(`Task ${id} is already ${task.status}`));
    return;
  }

  const success = bgManager.cancelTask(task.id);

  if (success) {
    console.log(kleur.green(`✅ Task cancelled: ${task.id.substring(0, 12)}`));
  } else {
    console.log(kleur.red(`Failed to cancel task: ${id}`));
  }
}

function deleteTask(id: string): void {
  const bgManager = getBackgroundAgentManager();
  const tasks = bgManager.getAllTasks();
  const task = tasks.find(t => t.id === id || t.id.startsWith(id));

  if (!task) {
    console.log(kleur.red(`Task not found: ${id}`));
    return;
  }

  const success = bgManager.deleteTask(task.id);

  if (success) {
    console.log(kleur.green(`✅ Task deleted: ${task.id.substring(0, 12)}`));
  } else {
    console.log(kleur.red(`Failed to delete task: ${id}`));
  }
}

function clearCompletedTasks(): void {
  const bgManager = getBackgroundAgentManager();
  const cleared = bgManager.clearCompletedTasks();

  if (cleared > 0) {
    console.log(kleur.green(`✅ Cleared ${cleared} completed task(s)`));
  } else {
    console.log(kleur.gray('No completed tasks to clear'));
  }
}

function showQueueStatus(): void {
  const bgManager = getBackgroundAgentManager();
  const status = bgManager.getQueueStatus();

  console.log(kleur.cyan('\n📊 Queue Status'));
  console.log(kleur.gray('─'.repeat(40)));
  console.log(kleur.yellow(`Pending:   ${status.pending}`));
  console.log(kleur.blue(`Running:   ${status.running}`));
  console.log(kleur.green(`Completed: ${status.completed}`));
  console.log(kleur.red(`Failed:    ${status.failed}`));
  console.log(kleur.gray('─'.repeat(40)));
  console.log(kleur.white(`Total:     ${status.total}`));
}

function showHelp(): void {
  console.log(kleur.cyan('\n📋 Tasks Command'));
  console.log(kleur.gray('─'.repeat(60)));
  console.log(kleur.white('Manage background agent tasks'));
  console.log();
  console.log(kleur.cyan('Starting Background Tasks:'));
  console.log(kleur.gray('  Prefix your prompt with & to run in background'));
  console.log(kleur.gray('  Example: & Refactor the authentication module'));
  console.log();
  console.log(kleur.cyan('Commands:'));
  console.log(kleur.gray('  /tasks               - List all tasks'));
  console.log(kleur.gray('  /tasks list          - List all tasks'));
  console.log(kleur.gray('  /tasks show <id>     - Show task details'));
  console.log(kleur.gray('  /tasks cancel <id>   - Cancel a task'));
  console.log(kleur.gray('  /tasks delete <id>   - Delete a task'));
  console.log(kleur.gray('  /tasks clear         - Clear completed tasks'));
  console.log(kleur.gray('  /tasks queue         - Show queue status'));
  console.log();
  console.log(kleur.cyan('Task IDs:'));
  console.log(kleur.gray('  You can use partial IDs (first 8+ characters)'));
}

function getStatusIcon(status: BackgroundTask['status']): string {
  switch (status) {
    case 'pending': return kleur.yellow('⏳');
    case 'running': return kleur.blue('🔄');
    case 'completed': return kleur.green('✅');
    case 'failed': return kleur.red('❌');
    case 'cancelled': return kleur.gray('🚫');
    default: return kleur.gray('❓');
  }
}

function createProgressBar(progress: number, width: number = 20): string {
  const filled = Math.round((progress / 100) * width);
  const empty = width - filled;
  return kleur.green('█'.repeat(filled)) + kleur.gray('░'.repeat(empty));
}

function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return date.toLocaleDateString();
}
